import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authApi } from '@/services/authApi';
import { TERMS_OF_SERVICE, PRIVACY_POLICY, type LegalDoc } from '@/data/legalContent';
import LegalModal from './LegalModal';
import AuthShell from './AuthShell';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { scrollToFirstError } from './formError';
import s from './Auth.module.scss';

// [2026-09-12] 검증 실패 시 첫 에러 필드 판정용 — 화면 위→아래 순서. 각 input엔 name=, 약관 블록엔 data-field= 로 매칭.
const FIELD_ORDER = ['username', 'password', 'password2', 'name', 'phone', 'email', 'terms'];

// 백엔드 규칙과 ★동일★하게(server PasswordRules·SignupController): 아이디 8~20 영숫자, 비번 8~20 영·숫·특수.
const USERNAME_RE = /^[a-zA-Z0-9]{8,20}$/;
// [2026-09-11 사용자] 이메일 = 영업 리드 자산 → 필수 + 형식검증(백엔드 SignupController와 동일). 소셜은 provider 검증이라 별도.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
function pwOk(pw: string) {
	if (pw.length < 8 || pw.length > 20) return false;
	let letter = false, digit = false, special = false;
	for (const c of pw) {
		if (/[a-zA-Z]/.test(c)) letter = true;
		else if (/[0-9]/.test(c)) digit = true;
		else special = true;
	}
	return letter && digit && special;
}
const OTP_SECONDS = 180;
// [2026-09-11 #4] 개인정보 유효기간 — autonics(1/3/5년/탈퇴시)와 동일. 값=개월(0=탈퇴시까지). 백엔드 normalizeRetention과 일치.
const RETENTIONS = [
	{ v: 0, label: '회원 탈퇴 시까지' },
	{ v: 12, label: '1년' },
	{ v: 36, label: '3년' },
	{ v: 60, label: '5년' },
];

/** [2026-09-10] 회원가입 — autonics 기획 구조. 휴대폰 SMS 본인인증 필수. */
export default function Signup() {
	const nav = useNavigate();
	const [username, setUsername] = useState('');
	const [unChecked, setUnChecked] = useState<null | boolean>(null); // null=미확인
	const [unMsg, setUnMsg] = useState('');
	const [password, setPassword] = useState('');
	const [password2, setPassword2] = useState('');
	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [done, setDone] = useState(false);   // [2026-09-12] 가입완료 = 네이티브 alert 대신 모달
	// 휴대폰 인증
	const [phone, setPhone] = useState('');
	const [otpSent, setOtpSent] = useState(false);
	const [code, setCode] = useState('');
	const [verified, setVerified] = useState(false);
	const [timer, setTimer] = useState(0);
	const [phoneMsg, setPhoneMsg] = useState('');
	// 약관
	const [t, setT] = useState({ terms: false, privacy: false, age14: false, sms: false, email: false });
	const [retention, setRetention] = useState(0);   // [2026-09-11 #4] 개인정보 유효기간(개월)
	const [modalDoc, setModalDoc] = useState<LegalDoc | null>(null);
	const [err, setErr] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [fe, setFe] = useState<Record<string, string>>({});   // 필드별 오류(빨간 테두리+텍스트)
	const clr = (k: string) => setFe((f) => (f[k] ? { ...f, [k]: '' } : f));
	const formRef = useRef<HTMLFormElement>(null);   // [2026-09-12] 첫 에러 필드 스크롤+포커스 대상 컨테이너

	// OTP 카운트다운
	useEffect(() => {
		if (!otpSent || verified || timer <= 0) return;
		const id = setInterval(() => setTimer((v) => (v <= 1 ? 0 : v - 1)), 1000);
		return () => clearInterval(id);
	}, [otpSent, verified, timer]);

	const allAgree = t.terms && t.privacy && t.age14 && t.sms && t.email;
	const toggleAll = (v: boolean) => setT({ terms: v, privacy: v, age14: v, sms: v, email: v });

	async function checkUsername() {
		if (!USERNAME_RE.test(username)) { setUnChecked(false); setUnMsg('아이디는 영문·숫자 8~20자입니다.'); return; }
		const r = await authApi.checkUsername(username);
		setUnChecked(!!r.available); setUnMsg(r.message || '');
	}

	async function sendOtp() {
		setPhoneMsg('');
		const r = await authApi.phoneSend(phone, 'SIGNUP');
		if (!r.success) { setPhoneMsg(r.message || '발송 실패'); return; }
		setOtpSent(true); setVerified(false); setTimer(OTP_SECONDS);
		setPhoneMsg('인증번호를 발송했습니다. 3분 이내 입력해주세요.');
	}
	async function verifyOtp() {
		const r = await authApi.phoneVerify(phone, code, 'SIGNUP');
		if (!r.success) { setPhoneMsg(r.message || '인증 실패'); return; }
		setVerified(true); setPhoneMsg('휴대폰 인증이 완료되었습니다.');
	}

	const mmss = useCallback((sec: number) => `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`, []);

	async function submit(e: React.FormEvent) {
		e.preventDefault();
		setErr(null);
		// [2026-09-10 사용자] alert 대신 문제 필드에 빨간 테두리(input.err)+필드별 오류 텍스트로 표시.
		const fx: Record<string, string> = {};
		if (!username) fx.username = '아이디를 입력해주세요.';
		else if (unChecked !== true) fx.username = unChecked === false ? (unMsg || '아이디를 확인해주세요.') : '아이디 중복확인을 해주세요.';
		if (!pwOk(password)) fx.password = '영문·숫자·특수문자 포함 8~20자로 입력해주세요.';
		if (password !== password2) fx.password2 = '비밀번호가 일치하지 않습니다.';
		if (!name.trim()) fx.name = '이름을 입력해주세요.';
		if (!email.trim()) fx.email = '이메일을 입력해주세요.';
		else if (!EMAIL_RE.test(email.trim())) fx.email = '올바른 이메일 형식이 아닙니다.';
		if (!verified) fx.phone = '휴대폰 본인인증을 완료해주세요.';
		if (!(t.terms && t.privacy && t.age14)) fx.terms = '필수 약관에 동의해주세요.';
		setFe(fx);
		// [2026-09-12] 에러 있으면 첫 빨간 필드로 스크롤+포커스(최상단 고정 이동 대신 — formError.ts 참고).
		if (Object.keys(fx).length > 0) { scrollToFirstError(formRef.current, FIELD_ORDER, fx); return; }

		setBusy(true);
		const r = await authApi.signup({
			username, password, name: name.trim(), phone, email: email.trim(),
			age14: t.age14, agreeTerms: t.terms, agreePrivacy: t.privacy, smsOpt: t.sms, emailOpt: t.email,
			retentionMonths: retention,   // [2026-09-11 #4] 개인정보 유효기간
		});
		setBusy(false);
		if (!r.success) return setErr(r.message || '회원가입에 실패했습니다.');
		setDone(true);   // [2026-09-12] 완료 모달 → [로그인하러 가기]에서 이동
	}

	const pwHint = password ? (pwOk(password) ? { c: s.ok, m: '사용 가능한 비밀번호입니다.' } : { c: s.bad, m: '영문·숫자·특수문자 포함 8~20자' }) : null;

	return (
		<AuthShell eyebrow="EVS MEMBERSHIP" title={<>회원가입</>}
			desc="안전과 환경, EVS와 함께 시작하세요.">
				<p className={s.hint} style={{ marginBottom: 18 }}>표시(<span className={s.req}>*</span>)는 필수 입력 항목입니다.</p>
				{err && <div className={`${s.msg} ${s.error}`}>{err}</div>}

				<form onSubmit={submit} ref={formRef}>
					{/* 아이디 */}
					<div className={s.field}>
						<label className={s.label}>아이디<span className={s.req}>*</span></label>
						<div className={s.inline}>
							<input name="username" className={`${s.input} ${fe.username ? s.err : ''}`} value={username}
								onChange={(e) => { setUsername(e.target.value); setUnChecked(null); setUnMsg(''); clr('username'); }}
								placeholder="영문·숫자 8~20자" />
							<button type="button" className={s.inlineBtn} onClick={checkUsername} disabled={!username}>중복확인</button>
						</div>
						{fe.username ? <p className={`${s.hint} ${s.bad}`}>{fe.username}</p>
							: unChecked !== null && <p className={`${s.hint} ${unChecked ? s.ok : s.bad}`}>{unMsg}</p>}
					</div>

					{/* 비밀번호 */}
					<div className={s.field}>
						<label className={s.label}>비밀번호<span className={s.req}>*</span></label>
						<input name="password" className={`${s.input} ${fe.password ? s.err : ''}`} type="password" value={password} onChange={(e) => { setPassword(e.target.value); clr('password'); }}
							placeholder="영문·숫자·특수문자 포함 8~20자" autoComplete="new-password" />
						{fe.password ? <p className={`${s.hint} ${s.bad}`}>{fe.password}</p> : pwHint && <p className={`${s.hint} ${pwHint.c}`}>{pwHint.m}</p>}
					</div>
					<div className={s.field}>
						<label className={s.label}>비밀번호 확인<span className={s.req}>*</span></label>
						<input name="password2" className={`${s.input} ${fe.password2 ? s.err : ''}`} type="password" value={password2} onChange={(e) => { setPassword2(e.target.value); clr('password2'); }}
							placeholder="비밀번호 재입력" autoComplete="new-password" />
						{fe.password2 && <p className={`${s.hint} ${s.bad}`}>{fe.password2}</p>}
						{!fe.password2 && password2 && <p className={`${s.hint} ${password === password2 ? s.ok : s.bad}`}>
							{password === password2 ? '비밀번호가 일치합니다.' : '비밀번호가 일치하지 않습니다.'}</p>}
					</div>

					{/* 이름 */}
					<div className={s.field}>
						<label className={s.label}>이름<span className={s.req}>*</span></label>
						<input name="name" className={`${s.input} ${fe.name ? s.err : ''}`} value={name} onChange={(e) => { setName(e.target.value); clr('name'); }} placeholder="이름" />
						{fe.name && <p className={`${s.hint} ${s.bad}`}>{fe.name}</p>}
					</div>

					{/* 휴대폰 인증 */}
					<div className={s.field}>
						<label className={s.label}>휴대폰 본인인증<span className={s.req}>*</span></label>
						<div className={s.inline}>
							<input name="phone" className={`${s.input} ${fe.phone ? s.err : ''}`} value={phone} onChange={(e) => { setPhone(e.target.value); clr('phone'); }}
								placeholder="휴대폰 번호 ('-' 없이)" inputMode="numeric" disabled={verified} />
							<button type="button" className={s.inlineBtn} onClick={sendOtp} disabled={!phone || verified}>
								{otpSent ? '재전송' : '인증 전송'}</button>
						</div>
						{otpSent && !verified && (
							<div className={s.inline} style={{ marginTop: 8 }}>
								<input className={s.input} value={code} onChange={(e) => setCode(e.target.value)}
									placeholder="인증번호 6자리" inputMode="numeric" maxLength={6} />
								<button type="button" className={s.inlineBtn} onClick={verifyOtp} disabled={!code}>확인</button>
							</div>
						)}
						{otpSent && !verified && timer > 0 &&
							<p className={s.hint}>남은 시간 <span className={s.timer}>{mmss(timer)}</span></p>}
						{phoneMsg && <p className={`${s.hint} ${verified ? s.ok : s.bad}`}>{phoneMsg}</p>}
						{fe.phone && !phoneMsg && <p className={`${s.hint} ${s.bad}`}>{fe.phone}</p>}
					</div>

					{/* 이메일(필수·형식검증) — 영업 리드로 활용 */}
					<div className={s.field}>
						<label className={s.label}>이메일<span className={s.req}>*</span></label>
						<input name="email" className={`${s.input} ${fe.email ? s.err : ''}`} type="email" value={email}
							onChange={(e) => { setEmail(e.target.value); clr('email'); }}
							placeholder="이메일 주소" autoComplete="email" />
						{fe.email && <p className={`${s.hint} ${s.bad}`}>{fe.email}</p>}
					</div>

					{/* 약관 */}
					<div className={s.terms} data-field="terms">
						<div className={`${s.termRow} ${s.termAll}`}>
							<input type="checkbox" id="all" checked={allAgree} onChange={(e) => toggleAll(e.target.checked)} />
							<label htmlFor="all">필수·선택 항목에 모두 동의합니다.</label>
						</div>
						<div className={s.termRow}>
							<input type="checkbox" id="tos" checked={t.terms} onChange={(e) => setT({ ...t, terms: e.target.checked })} />
							<label htmlFor="tos"><span className={s.tag}>[필수]</span> 이용약관 동의</label>
							<button type="button" className={s.detailBtn} onClick={() => setModalDoc(TERMS_OF_SERVICE)}>자세히 보기</button>
						</div>
						<div className={s.termRow}>
							<input type="checkbox" id="pp" checked={t.privacy} onChange={(e) => setT({ ...t, privacy: e.target.checked })} />
							<label htmlFor="pp"><span className={s.tag}>[필수]</span> 개인정보 처리방침 동의</label>
							<button type="button" className={s.detailBtn} onClick={() => setModalDoc(PRIVACY_POLICY)}>자세히 보기</button>
						</div>
						<div className={s.termRow}>
							<input type="checkbox" id="age" checked={t.age14} onChange={(e) => setT({ ...t, age14: e.target.checked })} />
							<label htmlFor="age"><span className={s.tag}>[필수]</span> 만 14세 이상입니다.</label>
						</div>
						<div className={s.termRow}>
							<input type="checkbox" id="sms" checked={t.sms} onChange={(e) => setT({ ...t, sms: e.target.checked })} />
							<label htmlFor="sms"><span className={s.tag}>[선택]</span> SMS 수신 동의</label>
						</div>
						<div className={s.termRow}>
							<input type="checkbox" id="eml" checked={t.email} onChange={(e) => setT({ ...t, email: e.target.checked })} />
							<label htmlFor="eml"><span className={s.tag}>[선택]</span> 이메일 수신 동의</label>
						</div>
					</div>
					{fe.terms && <p className={`${s.hint} ${s.bad}`} style={{ marginTop: -12, marginBottom: 16 }}>{fe.terms}</p>}

					{/* [2026-09-11 #4] 개인정보 유효기간 선택(autonics 참고) */}
					<div className={s.field}>
						<label className={s.label}>개인정보 유효기간<span className={s.req}>*</span></label>
						<div className={s.retRow}>
							{RETENTIONS.map((o) => (
								<button type="button" key={o.v}
									className={`${s.retBtn} ${retention === o.v ? s.retOn : ''}`}
									onClick={() => setRetention(o.v)}>{o.label}</button>
							))}
						</div>
					</div>

					<button className={s.primary} disabled={busy}>{busy ? '가입 처리 중…' : '가입하기'}</button>
				</form>

				<div className={s.links}>
					이미 회원이신가요? <Link to="/login">로그인</Link>
				</div>
			<LegalModal doc={modalDoc} onClose={() => setModalDoc(null)} />
			<ConfirmModal open={done} alertMode title="회원가입 완료" confirmText="로그인하러 가기"
				message="회원가입이 완료되었습니다. 로그인해주세요." onConfirm={() => nav('/login', { replace: true })} onCancel={() => {}} />
		</AuthShell>
	);
}
